import type { SwiperProps, SwiperRef, SwiperSlideProps } from 'swiper/react';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { SwiperModule } from 'swiper/types';

/* --------------------------------------------------------------------------------------
Types
-------------------------------------------------------------------------------------- */
type SwiperComponent = React.FunctionComponent<React.RefAttributes<SwiperRef> & React.PropsWithChildren<SwiperProps>>;

type SwiperSlideComponent = React.FunctionComponent<SwiperSlideProps>;

type UseLazySwiperArgs = {
    useSwiper: boolean;
};

type UseLazySwiperReturn = {
    isUsingSwiper: boolean;
    Swiper: SwiperComponent;
    SwiperSlide: SwiperSlideComponent;
    swiperModules: SwiperModule[];
};

/* --------------------------------------------------------------------------------------
Fallbacks
-------------------------------------------------------------------------------------- */
const SwiperFallback: SwiperComponent = forwardRef(({ children, className }, _ref) => {
    return <div className={className}>{children}</div>;
});

SwiperFallback.displayName = 'SwiperFallback';

const SwiperSlideFallback: SwiperSlideComponent = forwardRef(({ className, children }, _ref) => {
    // @ts-expect-error -- it's fine, we never use render as function
    return <div className={className}>{children}</div>;
});

SwiperSlideFallback.displayName = 'SwiperSlideFallback';

/* --------------------------------------------------------------------------------------
Hook
-------------------------------------------------------------------------------------- */
const useLazySwiper = ({ useSwiper }: UseLazySwiperArgs): UseLazySwiperReturn => {
    const swiperLoaded = useRef<boolean>(false);
    const [swiper, setSwiper] = useState<SwiperComponent | null>(SwiperFallback);
    const [swiperSlide, setSwiperSlide] = useState<SwiperSlideComponent>(SwiperSlideFallback);
    const [swiperModules, setSwiperModules] = useState<SwiperModule[]>([]);

    useEffect(() => {
        if (!useSwiper || swiperLoaded.current) {
            return;
        }

        const getSwiperModules = async () => {
            const [swiperComponents, { Thumbs }] = await Promise.all([
                import(/* webpackChunkName: "swiper" */ 'swiper/react'),
                import(/* webpackChunkName: "swiper" */ 'swiper/modules'),
                // @ts-expect-error -- css files are not modules
                import(/* webpackChunkName: "swiper-css" */ 'swiper/css'),
            ]);

            swiperLoaded.current = true;
            setSwiperModules([Thumbs] as SwiperModule[]);
            setSwiper(swiperComponents.Swiper);
            setSwiperSlide(swiperComponents.SwiperSlide);
        };

        getSwiperModules().catch(() => {
            /**
             * error handling
             */
        });
    }, [useSwiper]);

    const isUsingSwiper = useSwiper && swiperLoaded.current;

    return useMemo(
        () =>
            ({
                isUsingSwiper,
                Swiper: isUsingSwiper ? swiper : SwiperFallback,
                SwiperSlide: isUsingSwiper ? swiperSlide : SwiperSlideFallback,
                swiperModules: isUsingSwiper ? swiperModules : [],
            }) as UseLazySwiperReturn,
        [isUsingSwiper, swiper, swiperSlide, swiperModules],
    );
};

/* --------------------------------------------------------------------------------------
Exports
-------------------------------------------------------------------------------------- */
export { useLazySwiper };
export type { UseLazySwiperArgs, UseLazySwiperReturn };
